import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileDiffIcon,
  FilePlus2Icon,
  FileSignatureIcon,
  UploadIcon,
} from 'lucide-react';
import { Button, ConfirmDialog, DatePicker, EmptyState, ErrorNotice, Field, LoadingState, Panel, Pill, SegmentedControl, TextInput } from '@bcecloud/portal-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { errorMessage } from '@/lib/api';
import { readEvent, type EventContact } from '@/lib/events';
import {
  contractPaths,
  generateContract,
  listContracts,
  publishContract,
  readContractLibrary,
  uploadContract,
  voidContract,
  type Contract,
  type ContractStatus,
  type GenerateContractInput,
  resendContractInvite,
  readSigningLink,
  importRedlines,
  describeRedlines,
  reviewRedline,
  applyRedlines,
  readRedlineApprovers,
  requestRedlineApproval,
  type RedlineProposal,
  type ContractSubject,
  CONTRACT_STATUS_LABELS as STATUS_LABELS,
  CONTRACT_STATUS_TONES as STATUS_TONES,
  CONTRACT_SERIES_RESULT,
  CONTRACT_SERIES_UNAVAILABLE,
  defaultAttachmentB,
  readContractSeries,
  seriesCoverage,
  type ContractSeriesView,
} from '@/lib/contracts';
import { formatShortDate } from '@/proposals/lib/money';
import { getJson } from '@/lib/api';

/** Statuses from which the builder can still edit the document. */
const EDITABLE: ReadonlySet<ContractStatus> = new Set([
  'draft',
  'client_review',
  'changes_requested',
  'approved',
]);

/** Statuses from which a contract can be published to the client portal. */
const PUBLISHABLE: ReadonlySet<ContractStatus> = new Set([
  'draft',
  'approved',
  'changes_requested',
]);

/**
 * The Contracts tab of an event, or the Agreement tab of a campaign:
 * generate or upload a draft, adjust it in the full-page builder, run the
 * client review/redline loop, send for signature, and watch signing
 * progress (updated by provider webhooks). Contracts render as list rows —
 * one per document/revision stream — with their actions on the right.
 */
export function ContractsTab({ subject }: { readonly subject: ContractSubject }): React.ReactElement {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  // A campaign's or a package request's contract both live on the client's portal.
  const isCampaign = subject.kind !== 'event';

  const contracts = useQuery({
    queryKey: ['contracts', subject.kind, subject.id],
    queryFn: ({ signal }) => listContracts(subject, signal),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['contracts', subject.kind, subject.id] });
    if (subject.kind === 'campaign') void queryClient.invalidateQueries({ queryKey: ['campaign', subject.id] });
    // The package request's status follows the contract, so the Agreement tab re-reads it.
    if (subject.kind === 'agreement') void queryClient.invalidateQueries({ queryKey: ['org-agreement', subject.organizationId] });
  };

  const generate = useMutation({
    // A series contract is saved on the parent's event, which may not be the
    // one this tab is showing; it lists here too because it covers this event.
    mutationFn: ({ input, target }: { input: GenerateContractInput; target: ContractSubject }) =>
      generateContract(target, input),
    onSuccess: refresh,
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadContract(subject, file),
    onSuccess: refresh,
  });

  if (contracts.isPending) return <LoadingState label="Loading contracts…" />;
  if (contracts.isError) {
    return <ErrorNotice title="Contracts unavailable" message={errorMessage(contracts.error)} />;
  }

  const isEmpty = contracts.data.length === 0;

  const actions = (size: 'default' | 'compact') => (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <Button size={size} onClick={() => setGenerateOpen(true)} disabled={generate.isPending}>
        <FilePlus2Icon aria-hidden className="size-4" />
        {generate.isPending ? 'Generating…' : subject.kind === 'agreement' ? 'Generate from the request' : isCampaign ? 'Generate from campaign' : 'Generate from event'}
      </Button>
      <Button
        size={size}
        variant="secondary"
        onClick={() => fileInput.current?.click()}
        disabled={upload.isPending}
      >
        <UploadIcon aria-hidden className="size-4" />
        {upload.isPending ? 'Uploading…' : 'Upload PDF/DOCX'}
      </Button>
    </div>
  );

  return (
    <div className="mt-6 space-y-3">
      <input
        ref={fileInput}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate(file);
          e.target.value = '';
        }}
      />

      {isEmpty ? null : <div className="flex justify-end">{actions('compact')}</div>}

      {generate.isError ? (
        <ErrorNotice inline title="Generate failed" message={errorMessage(generate.error)} />
      ) : null}
      {upload.isError ? (
        <ErrorNotice inline title="Upload failed" message={errorMessage(upload.error)} />
      ) : null}

      {generateOpen ? (
        <GenerateDialog
          subject={subject}
          onClose={() => setGenerateOpen(false)}
          onGenerate={(input, target) => {
            setGenerateOpen(false);
            generate.mutate({ input, target });
          }}
        />
      ) : null}

      {isEmpty ? (
        <Panel className="p-10">
          <EmptyState
            icon={<FileSignatureIcon aria-hidden className="size-5" />}
            title={subject.kind === 'agreement' ? 'No agreement drafted for this request yet' : isCampaign ? 'No agreement for this campaign yet' : 'No contracts for this event yet'}
            message={
              subject.kind === 'agreement'
                ? 'Generate the enterprise agreement from the request — the client, the package, the price at their discount and the term — then fine-tune the wording and signing fields. Or upload your own PDF/DOCX. Share it with the client for review, work through any redlines, and send it for signature; the credits enter their pool when it is signed.'
                : isCampaign
                ? 'Generate a draft from the campaign — the client, the program, and every event under it — then fine-tune the wording and signing fields before it goes anywhere. Or upload your own PDF/DOCX and we’ll convert it into an editable draft. From there: share it with the client for review, work through any redlines, and send it for signature. It can go out alongside the proposal.'
                : "Generate a draft from the event's details — program, date, venue, and payment terms — then fine-tune the wording and signing fields before it goes anywhere. Or upload your own PDF/DOCX and we’ll convert it into an editable draft. From there: share it with the client for review, work through any redlines, and send it for signature."
            }
            action={actions('default')}
          />
        </Panel>
      ) : (
        <Panel className="divide-y divide-portal-line p-0">
          {contracts.data.map((contract) => (
            <ContractRow
              key={contract._id}
              contract={contract}
              subject={subject}
              onChanged={refresh}
            />
          ))}
        </Panel>
      )}
    </div>
  );
}

/** One contract row: facts on the left, its allowed actions on the right. */
function ContractRow({
  contract,
  subject,
  onChanged,
}: {
  readonly contract: Contract;
  readonly subject: ContractSubject;
  readonly onChanged: () => void;
}): React.ReactElement {
  const paths = contractPaths(subject);
  const [publishOpen, setPublishOpen] = useState(false);
  const [confirmVoid, setConfirmVoid] = useState(false);
  const [linkCopy, setLinkCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [resendOpen, setResendOpen] = useState(false);
  const [redlinesOpen, setRedlinesOpen] = useState(false);
  const pendingRedlines = (contract.redlineProposals ?? []).filter(
    (proposal) => proposal.status === 'proposed' || proposal.status === 'accepted',
  ).length;

  /** An event contract's signing link is copied from the server, so it matches the invite email. */
  const canCopySigningLink = subject.kind === 'event';

  const doVoid = useMutation({
    mutationFn: () => voidContract(contract._id),
    onSuccess: () => {
      setConfirmVoid(false);
      onChanged();
    },
  });

  const signer = contract.submitters[0];
  const hasDetail =
    contract.negotiation.length > 0 ||
    (signer !== undefined && (contract.status === 'sent' || contract.status === 'viewed')) ||
    (contract.status === 'signed' && contract.documents.length > 0);

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-portal-body font-bold text-portal-ink">{contract.name}</p>
            <Pill tone={STATUS_TONES[contract.status]}>{STATUS_LABELS[contract.status]}</Pill>
            <span
              className="font-mono text-portal-micro text-portal-muted"
              title="Quote this ref to the assistant — e.g. “edit the contract ending in this code”"
            >
              Ref {contract._id.slice(-6).toUpperCase()}
            </span>
          </div>
          <p className="text-portal-small text-portal-muted">
            Revision {contract.revision} ·{' '}
            {contract.source === 'uploaded' ? 'Uploaded' : 'Generated'}
            {contract.additionalEventIds?.length
              ? ` · covers ${contract.additionalEventIds.length + 1} events in the series`
              : ''}
            {contract.signedAt
              ? ` · signed ${new Date(contract.signedAt).toLocaleDateString()}`
              : contract.sentAt
                ? ` · sent ${new Date(contract.sentAt).toLocaleDateString()}`
                : ` · created ${new Date(contract.createdAt).toLocaleDateString()}`}
            {signer?.openedAt
              ? ` · opened by signer ${new Date(signer.openedAt).toLocaleString()}`
              : contract.status === 'sent'
                ? ' · not yet opened'
                : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="compact"
            variant="secondary"
            to={paths.page(contract._id, 'preview')}
          >
            Preview
          </Button>
          {EDITABLE.has(contract.status) ? (
            <Button
              size="compact"
              variant="secondary"
              to={paths.page(contract._id, 'text')}
            >
              Edit
            </Button>
          ) : null}
          {PUBLISHABLE.has(contract.status) ? (
            <>
              <Button
                size="compact"
                variant="secondary"
                title="Import a client's tracked-changes Word document as reviewable redlines"
                onClick={() => setRedlinesOpen(true)}
              >
                Redlines
                {pendingRedlines > 0 ? ` (${pendingRedlines})` : ''}
              </Button>
              <Button size="compact" onClick={() => setPublishOpen(true)}>
                Publish to portal
              </Button>
            </>
          ) : null}
          {(contract.status === 'sent' || contract.status === 'viewed') &&
          !contract.submitters.some((s) => s.completedAt) ? (
            <Button
              size="compact"
              variant="secondary"
              title="Re-issue the signing request (e.g. if the signing link stopped working)"
              onClick={() => setPublishOpen(true)}
            >
              Re-publish
            </Button>
          ) : null}
          {(contract.status === 'sent' || contract.status === 'viewed') &&
          !contract.submitters.some((s) => s.completedAt) ? (
            <>
              <Button
                size="compact"
                variant="secondary"
                title="Send the branded invite email again"
                onClick={() => setResendOpen(true)}
              >
                Resend invite
              </Button>
              {canCopySigningLink ? (
                <Button
                  size="compact"
                  variant="secondary"
                  title="Copy the link the invite email carries"
                  onClick={() => {
                    void readSigningLink(contract._id)
                      .then(async (link) => {
                        await navigator.clipboard.writeText(link.url);
                        setLinkCopy('copied');
                      })
                      .catch(() => setLinkCopy('failed'))
                      .finally(() => setTimeout(() => setLinkCopy('idle'), 2000));
                  }}
                >
                  {linkCopy === 'copied' ? 'Link copied' : linkCopy === 'failed' ? 'Could not copy' : 'Copy signing link'}
                </Button>
              ) : null}
            </>
          ) : null}
          {(contract.status === 'sent' || contract.status === 'viewed') &&
          contract.submitters.some((s) => s.role === 'BCE' && !s.completedAt) ? (
            <Button
              size="compact"
              to={paths.page(contract._id, 'countersign')}
            >
              Countersign
            </Button>
          ) : null}
          {contract.status !== 'signed' && contract.status !== 'voided' ? (
            <Button
              size="compact"
              variant="secondary"
              className="border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => setConfirmVoid(true)}
            >
              Void
            </Button>
          ) : null}
        </div>
      </div>

      {hasDetail ? (
        <div className="mt-3 space-y-2">
          {contract.negotiation.length > 0 ? (
            <div className="space-y-1 rounded-portal-button bg-portal-bg p-3">
              {contract.negotiation.map((entry, i) => (
                <p key={i} className="text-portal-small text-portal-ink">
                  <strong>
                    {entry.author === 'client'
                      ? (entry.authorName ?? 'Client')
                      : (entry.authorName ?? 'BCE')}
                    :
                  </strong>{' '}
                  {entry.message}
                </p>
              ))}
            </div>
          ) : null}

          {signer !== undefined &&
          (contract.status === 'sent' || contract.status === 'viewed') ? (
            <p className="text-portal-small text-portal-muted">
              Signer: {signer.name ?? signer.email}
              {signer.openedAt
                ? ` · opened ${new Date(signer.openedAt).toLocaleString()}`
                : ' · not opened yet'}
            </p>
          ) : null}

          {contract.status === 'signed' && contract.documents.length > 0 ? (
            <p className="text-portal-small">
              {contract.documents.map((doc) => (
                <a
                  key={doc.url}
                  href={doc.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mr-3 font-bold text-portal-accent hover:underline"
                >
                  {doc.name}
                </a>
              ))}
            </p>
          ) : null}
        </div>
      ) : null}

      {redlinesOpen ? (
        <RedlinesDialog
          contract={contract}
          onClose={() => setRedlinesOpen(false)}
          onChanged={onChanged}
        />
      ) : null}

      {resendOpen ? (
        <ResendInviteDialog
          contract={contract}
          onClose={() => setResendOpen(false)}
          onSent={() => {
            setResendOpen(false);
            onChanged();
          }}
        />
      ) : null}

      {publishOpen ? (
        <PublishDialog
          contract={contract}
          subject={subject}
          onClose={() => setPublishOpen(false)}
          onPublished={() => {
            setPublishOpen(false);
            onChanged();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirmVoid}
        title="Void this contract?"
        message="The draft and any pending signature round become unusable. This cannot be undone."
        confirmLabel="Void contract"
        tone="danger"
        pending={doVoid.isPending}
        onConfirm={() => doVoid.mutate()}
        onCancel={() => setConfirmVoid(false)}
      />
    </div>
  );
}

/** Renders one proposal as a readable diff. */
function RedlineDiff({ proposal }: { readonly proposal: RedlineProposal }): React.ReactElement {
  return (
    <span className="text-portal-small leading-6">
      {proposal.contextBefore ? (
        <span className="text-portal-muted">…{proposal.contextBefore.slice(-40)}</span>
      ) : null}
      {proposal.originalText ? (
        <span className="bg-red-50 text-red-700 line-through">{proposal.originalText}</span>
      ) : null}
      {proposal.proposedText ? (
        <span className="bg-green-50 text-green-800">{proposal.proposedText}</span>
      ) : null}
      {proposal.contextAfter ? (
        <span className="text-portal-muted">{proposal.contextAfter.slice(0, 40)}…</span>
      ) : null}
    </span>
  );
}

/**
 * Client redlines for one contract: import a tracked-changes Word document,
 * review each change as a diff, accept or reject, then apply the accepted
 * set through the canonical edit pipeline. Unlocatable passages come back
 * as "unmatched" rather than being guessed at.
 */
function RedlinesDialog({
  contract,
  onClose,
  onChanged,
}: {
  readonly contract: Contract;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): React.ReactElement {
  const fileInput = useRef<HTMLInputElement>(null);
  const [current, setCurrent] = useState<Contract>(contract);
  const proposals = current.redlineProposals ?? [];

  const doImport = useMutation({
    mutationFn: (file: File) => importRedlines(contract._id, file),
    onSuccess: (updated) => {
      setCurrent(updated);
      onChanged();
    },
  });
  const [description, setDescription] = useState('');
  const doDescribe = useMutation({
    mutationFn: () => describeRedlines(contract._id, description),
    onSuccess: (updated) => {
      setCurrent(updated);
      setDescription('');
      onChanged();
    },
  });
  const doReview = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'accepted' | 'rejected' }) =>
      reviewRedline(contract._id, id, status),
    onSuccess: (updated) => setCurrent(updated),
  });
  const doApply = useMutation({
    mutationFn: () => applyRedlines(contract._id),
    onSuccess: ({ contract: updated }) => {
      setCurrent(updated);
      onChanged();
    },
  });
  const approvers = useQuery({
    queryKey: ['redline-approvers'],
    queryFn: ({ signal }) => readRedlineApprovers(signal),
    staleTime: 5 * 60 * 1000,
  });
  const [approver, setApprover] = useState('');
  const doRequestApproval = useMutation({
    mutationFn: () => requestRedlineApproval(contract._id, approver),
    onSuccess: (updated) => {
      setCurrent(updated);
      onChanged();
    },
  });
  const approval = current.redlineApproval;

  const acceptedCount = proposals.filter((p) => p.status === 'accepted').length;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogTitle>Client redlines</DialogTitle>
        <p className="mt-1 text-portal-small text-portal-muted">
          Upload the client&apos;s redlined Word document (Track Changes on), or describe the
          requested changes — pasted from an email works. Every change becomes a proposal
          below — accept or reject each, then apply the accepted set.
        </p>

        <input
          ref={fileInput}
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          aria-label="Upload redlined document"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) doImport.mutate(file);
            event.target.value = '';
          }}
        />
        <div className="mt-3 flex items-center gap-2">
          <Button
            variant="secondary"
            size="compact"
            disabled={doImport.isPending}
            onClick={() => fileInput.current?.click()}
          >
            {doImport.isPending ? 'Reading…' : 'Upload redlined .docx'}
          </Button>
          {acceptedCount > 0 ? (
            <Button size="compact" disabled={doApply.isPending} onClick={() => doApply.mutate()}>
              {doApply.isPending ? 'Applying…' : `Apply ${acceptedCount} accepted`}
            </Button>
          ) : null}
          {acceptedCount > 0 && (approvers.data ?? []).length > 0 ? (
            <>
              <select
                aria-label="Approver"
                className="rounded-portal-button border bg-white px-2 py-1.5 text-portal-small"
                value={approver}
                onChange={(event) => setApprover(event.target.value)}
              >
                <option value="">Choose approver…</option>
                {(approvers.data ?? []).map((email) => (
                  <option key={email} value={email}>
                    {email}
                  </option>
                ))}
              </select>
              <Button
                size="compact"
                variant="secondary"
                disabled={approver === '' || doRequestApproval.isPending}
                onClick={() => doRequestApproval.mutate()}
              >
                {doRequestApproval.isPending ? 'Sending…' : 'Send for approval'}
              </Button>
            </>
          ) : null}
        </div>
        <div className="mt-3">
          <label
            htmlFor="redline-description"
            className="mb-1 block text-portal-small text-portal-muted"
          >
            Or describe the changes (e.g. paste the client&apos;s email)
          </label>
          <textarea
            id="redline-description"
            rows={3}
            className="w-full rounded-portal-button border bg-white p-2 text-portal-small"
            placeholder="Extend the cancellation window from 30 to 45 days, and change the deposit to 25%…"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <Button
            variant="secondary"
            size="compact"
            className="mt-1"
            disabled={description.trim().length < 8 || doDescribe.isPending}
            onClick={() => doDescribe.mutate()}
          >
            {doDescribe.isPending ? 'Analyzing…' : 'Analyze changes'}
          </Button>
        </div>
        {doDescribe.isError ? (
          <ErrorNotice inline title="Could not analyze" message={errorMessage(doDescribe.error)} />
        ) : null}
        {approval?.status ? (
          <p className="mt-2 text-portal-small text-portal-muted">
            Approval {approval.status}
            {approval.approverEmail ? ` · ${approval.approverEmail}` : ''}
            {approval.reason ? ` — “${approval.reason}”` : ''}
          </p>
        ) : null}
        {doRequestApproval.isError ? (
          <ErrorNotice inline title="Could not send" message={errorMessage(doRequestApproval.error)} />
        ) : null}
        {doImport.isError ? (
          <ErrorNotice inline title="Import failed" message={errorMessage(doImport.error)} />
        ) : null}
        {doApply.isError ? (
          <ErrorNotice inline title="Apply failed" message={errorMessage(doApply.error)} />
        ) : null}
        {doApply.isSuccess ? (
          <p className="mt-2 text-portal-small text-portal-muted">
            Applied {doApply.data.applied}
            {doApply.data.unmatched > 0
              ? ` — ${doApply.data.unmatched} could not be located and are marked unmatched; edit those by hand.`
              : '. Document re-rendered with a fresh signing template.'}
          </p>
        ) : null}

        <div className="mt-4 space-y-3">
          {proposals.length === 0 ? (
            <EmptyState
              icon={<FileDiffIcon aria-hidden className="size-5" />}
              title="No redlines yet"
              message="Upload the client's tracked-changes document or describe the requested changes above."
            />
          ) : null}
          {proposals.map((proposal) => (
            <div key={proposal._id} className="rounded-portal-button border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-portal-micro font-bold uppercase tracking-wide text-portal-muted">
                  {proposal.kind}
                  {proposal.author ? ` · ${proposal.author}` : ''}
                  {proposal.sourceName ? ` · ${proposal.sourceName}` : ''}
                </span>
                {proposal.status === 'proposed' || proposal.status === 'accepted' || proposal.status === 'rejected' ? (
                  <span className="flex gap-1">
                    <Button
                      size="compact"
                      variant={proposal.status === 'accepted' ? 'primary' : 'secondary'}
                      onClick={() => doReview.mutate({ id: proposal._id, status: 'accepted' })}
                    >
                      Accept
                    </Button>
                    <Button
                      size="compact"
                      variant={proposal.status === 'rejected' ? 'primary' : 'secondary'}
                      onClick={() => doReview.mutate({ id: proposal._id, status: 'rejected' })}
                    >
                      Reject
                    </Button>
                  </span>
                ) : (
                  <Pill tone={proposal.status === 'applied' ? 'success' : 'warning'}>
                    {proposal.status}
                  </Pill>
                )}
              </div>
              <div className="mt-2">
                <RedlineDiff proposal={proposal} />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Confirm-and-send for re-inviting the outstanding signer: shows exactly
 * who receives it and the message inside the branded email, editable
 * before sending.
 */
function ResendInviteDialog({
  contract,
  onClose,
  onSent,
}: {
  readonly contract: Contract;
  readonly onClose: () => void;
  readonly onSent: () => void;
}): React.ReactElement {
  const signer = contract.submitters.find((s) => s.role !== 'BCE' && !s.completedAt);
  const [message, setMessage] = useState(
    `Your contract is ready to review and sign: ${contract.name}.`,
  );
  const send = useMutation({
    mutationFn: () => resendContractInvite(contract._id, message),
    onSuccess: onSent,
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogTitle>Resend signing invite</DialogTitle>
        <p className="mt-1 text-portal-small text-portal-muted">
          Sends the branded invite email again, linking straight to the signing panel in
          My Events.
        </p>
        <div className="mt-3 rounded-portal-button border bg-portal-bg px-3 py-2 text-portal-small text-portal-ink">
          To: <b>{signer?.name || 'Signer'}</b>
          {signer?.email ? <span className="text-portal-muted"> &lt;{signer.email}&gt;</span> : null}
        </div>
        <Field
          label="Invite message"
          htmlFor={`resend-msg-${contract._id}`}
          hint="Your note inside the branded email — greeting, sign-in instructions, and the Review & sign button are added around it."
        >
          <textarea
            id={`resend-msg-${contract._id}`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="w-full resize-y rounded-portal-button border bg-white p-2 text-portal-small text-portal-ink focus-visible:outline-none focus-visible:ring-2"
          />
        </Field>
        {send.isError ? (
          <ErrorNotice inline title="Could not send" message={errorMessage(send.error)} />
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={send.isPending || !signer?.email} onClick={() => send.mutate()}>
            {send.isPending ? 'Sending…' : 'Send invite'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Publish a contract to the client's My Events portal: pick the tenant
 * organization (if not already set) and the signer, then it appears in the
 * portal's Contracts section ready to view and sign. One step — no separate
 * "share" then "send".
 */
/** An organization member as the admin API lists them; the campaign signer defaults to an admin. */
interface OrganizationMember {
  readonly id: string;
  readonly email: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly role: string;
  readonly isActive?: boolean;
}

function PublishDialog({
  contract,
  subject,
  onClose,
  onPublished,
}: {
  readonly contract: Contract;
  readonly subject: ContractSubject;
  readonly onClose: () => void;
  readonly onPublished: () => void;
}): React.ReactElement {
  // A campaign's or a package request's contract both live on the client's portal.
  const isCampaign = subject.kind !== 'event';
  // The invited signer defaults to the event's designated Signer contact,
  // then the primary contact — or, for a campaign, the client's first
  // organization admin — resolved before the form renders so the fields
  // SHOW who will be invited (still editable).
  const event = useQuery({
    queryKey: ['event-publish-contacts', subject.id],
    queryFn: ({ signal }) => readEvent(subject.id, signal),
    staleTime: 60_000,
    enabled: !isCampaign,
  });
  const members = useQuery({
    queryKey: ['organization-members', contract.organizationId ?? ''],
    queryFn: ({ signal }) => getJson<OrganizationMember[]>(`/organizations/${contract.organizationId ?? ''}/members`, undefined, signal),
    staleTime: 60_000,
    enabled: isCampaign && Boolean(contract.organizationId),
  });
  if (isCampaign) {
    if (members.isPending && Boolean(contract.organizationId)) {
      return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
          <DialogContent className="max-w-md">
            <DialogTitle>Publish to client portal</DialogTitle>
            <div className="mt-4">
              <LoadingState label="Looking up the client's admins…" />
            </div>
          </DialogContent>
        </Dialog>
      );
    }
    const admin = (members.data ?? []).find((member) => member.role === 'ORG_ADMIN' && member.isActive !== false && member.email);
    return (
      <PublishDialogForm
        contract={contract}
        isCampaign
        defaultSigner={admin ? { name: `${admin.firstName ?? ''} ${admin.lastName ?? ''}`.trim(), email: admin.email } : undefined}
        onClose={onClose}
        onPublished={onPublished}
      />
    );
  }
  if (event.isPending) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md">
          <DialogTitle>Publish to client portal</DialogTitle>
          <div className="mt-4">
            <LoadingState label="Looking up the event's signer…" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  const contacts: readonly EventContact[] = event.data?.contacts ?? [];
  const defaultSigner =
    contacts.find((c) => /signer/i.test(c.role ?? '') && c.email) ??
    contacts.find((c) => c.isPrimary && c.email) ??
    contacts.find((c) => c.email) ??
    event.data?.contact;
  return (
    <PublishDialogForm
      contract={contract}
      defaultSigner={defaultSigner}
      onClose={onClose}
      onPublished={onPublished}
    />
  );
}

function PublishDialogForm({
  contract,
  defaultSigner,
  isCampaign = false,
  onClose,
  onPublished,
}: {
  readonly contract: Contract;
  readonly defaultSigner: Pick<EventContact, 'name' | 'email'> | undefined;
  readonly isCampaign?: boolean;
  readonly onClose: () => void;
  readonly onPublished: () => void;
}): React.ReactElement {
  const [signerName, setSignerName] = useState(defaultSigner?.name ?? '');
  const [signerEmail, setSignerEmail] = useState(defaultSigner?.email ?? '');
  const [emailSigner, setEmailSigner] = useState(false);
  const [inviteMessage, setInviteMessage] = useState(
    `Your contract is ready to review and sign: ${contract.name}.`,
  );

  const publish = useMutation({
    mutationFn: () =>
      publishContract(contract._id, {
        ...(signerName.trim() ? { signerName: signerName.trim() } : {}),
        ...(signerEmail.trim() ? { signerEmail: signerEmail.trim() } : {}),
        emailSigner,
        ...(emailSigner && inviteMessage.trim() ? { inviteMessage: inviteMessage.trim() } : {}),
      }),
    onSuccess: onPublished,
  });

  // Email is optional; if provided it must look valid. Blank uses the
  // event's primary contact, and any event contact can sign either way.
  const valid = signerEmail.trim() === '' || /\S+@\S+\.\S+/.test(signerEmail);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogTitle>Publish to client portal</DialogTitle>
        <p className="mt-1 text-portal-small text-portal-muted">
          {isCampaign
            ? "The client sees this on their portal (the Agreement page for a package request, the campaign's Agreement tab for a campaign, and their Contracts page), where any member can view and sign it. The invited signer defaults to whoever asked, else one of their organization admins — adjust below if someone else should sign."
            : "The client sees this in My Events (the event's Contracts tab), where any contact on the event can view and sign it. The invited signer comes from the event's Signer role, then its primary contact — adjust below if someone else should sign."}
        </p>
        <form
          className="mt-3 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !publish.isPending) publish.mutate();
          }}
        >
          <Field
            label="Invited signer name"
            htmlFor={`pub-name-${contract._id}`}
          >
            <TextInput
              id={`pub-name-${contract._id}`}
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Signer name"
            />
          </Field>
          <Field label="Invited signer email" htmlFor={`pub-email-${contract._id}`}>
            <TextInput
              id={`pub-email-${contract._id}`}
              type="email"
              value={signerEmail}
              onChange={(e) => setSignerEmail(e.target.value)}
              placeholder="Signer email"
            />
          </Field>
          <label className="flex items-center gap-2 text-portal-small text-portal-ink">
            <Checkbox
              checked={emailSigner}
              onCheckedChange={(state) => setEmailSigner(state === true)}
            />
            Email the signer a branded invite (opens {isCampaign ? 'their portal' : 'My Events'} with the document ready to sign)
          </label>
          {emailSigner ? (
            <Field
              label={`Invite message (sent to ${signerEmail.trim() || 'the signer'})`}
              htmlFor={`invite-msg-${contract._id}`}
              hint="Your note inside the branded email — greeting, sign-in instructions, and the Review & sign button are added around it."
            >
              <textarea
                id={`invite-msg-${contract._id}`}
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                rows={3}
                className="w-full resize-y rounded-portal-button border bg-white p-2 text-portal-small text-portal-ink focus-visible:outline-none focus-visible:ring-2"
              />
            </Field>
          ) : null}
          {publish.isError ? (
            <ErrorNotice inline title="Publish failed" message={errorMessage(publish.error)} />
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || publish.isPending}>
              {publish.isPending ? 'Publishing…' : 'Publish'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/**
 * The events a series contract covers, one checkbox per Salesforce
 * opportunity in the series. The event the contract is saved on is always
 * covered; opportunities BCEcloud has no event for yet are listed but can't
 * be ticked.
 */
function SeriesFieldset({
  series,
  currentEventId,
  excludedEventIds,
  onToggle,
}: {
  readonly series: Extract<ContractSeriesView, { result: 'series' }>;
  readonly currentEventId: string;
  readonly excludedEventIds: ReadonlySet<string>;
  readonly onToggle: (eventId: string) => void;
}): React.ReactElement {
  const savedElsewhere = series.primaryEventId !== currentEventId;
  return (
    <fieldset>
      <legend className="text-portal-small font-bold text-portal-ink">Events on this contract</legend>
      <p className="mt-0.5 text-portal-micro text-portal-muted">
        This opportunity is part of a series in Salesforce. One contract covers the series, each event as
        “Event N of M”, and the totals add up across them.
        {savedElsewhere ? ' It’s saved on the parent’s event and shows on every event it covers.' : ''}
      </p>
      <div className="mt-2 space-y-1">
        {series.members.map((member) => {
          const isPrimary = member.eventId === series.primaryEventId;
          const label = [member.contractNumber || member.name || 'Opportunity', formatShortDate(member.eventDate), member.stageName]
            .filter(Boolean)
            .join(' · ');
          return (
            <label key={member.opportunityId} className="flex items-center gap-2 text-portal-small">
              <Checkbox
                checked={member.eventId !== null && !excludedEventIds.has(member.eventId)}
                disabled={member.eventId === null || isPrimary}
                onCheckedChange={() => member.eventId && onToggle(member.eventId)}
              />
              <span>
                {label}
                {member.isParent ? <span className="text-portal-muted"> (parent)</span> : null}
                {member.eventId === null ? <span className="text-portal-muted"> (not in BCEcloud yet)</span> : null}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * Pick what goes into a generated contract: the kind (standard vs virtual
 * defaults), the numbered clause sections, and the Attachment B purchaser
 * responsibilities — the portal port of Salesforce's per-opportunity
 * Contract_Sections__c + Attachment B selection.
 */
function GenerateDialog({
  subject,
  onClose,
  onGenerate,
}: {
  readonly subject: ContractSubject;
  readonly onClose: () => void;
  readonly onGenerate: (input: GenerateContractInput, target: ContractSubject) => void;
}): React.ReactElement {
  // A campaign's or a package request's contract both live on the client's portal.
  const isCampaign = subject.kind !== 'event';
  const eventId = subject.id;
  const [kind, setKind] = useState<'standard' | 'virtual' | 'enterprise'>(subject.kind === 'agreement' ? 'enterprise' : 'standard');
  const [checkedSections, setCheckedSections] = useState<readonly string[] | null>(null);
  // Null until someone changes Attachment B: until then it follows Salesforce's
  // picks for the covered events (see defaultAttachmentB).
  const [editedAttachmentB, setEditedAttachmentB] = useState<readonly string[] | null>(null);
  const [priceValidUntil, setPriceValidUntil] = useState('');
  const [excludedEventIds, setExcludedEventIds] = useState<ReadonlySet<string>>(new Set());

  // The Salesforce series this event belongs to: one contract covers the
  // parent opportunity and its children, each as "Event N of M" (S-Docs parity).
  const series = useQuery({
    queryKey: ['contract-series', eventId],
    queryFn: ({ signal }) => readContractSeries(eventId, signal),
    enabled: !isCampaign,
    staleTime: 60_000,
  });
  const seriesView = series.data?.result === CONTRACT_SERIES_RESULT.series ? series.data : null;
  const coverage = seriesCoverage(series.data, eventId, excludedEventIds);
  const target: ContractSubject = isCampaign ? subject : { kind: 'event', id: coverage.primaryEventId };

  const library = useQuery({
    queryKey: ['contract-library'],
    queryFn: ({ signal }) => readContractLibrary(signal),
    staleTime: 5 * 60 * 1000,
  });

  const defaults = library.data?.defaults[kind] ?? [];
  const sections = checkedSections ?? defaults;
  const salesforceAttachmentB = defaultAttachmentB(
    series.data,
    [coverage.primaryEventId, ...coverage.additionalEventIds],
    library.data?.attachmentB ?? [],
  );
  const checkedAttachmentB = editedAttachmentB ?? salesforceAttachmentB;

  /** Defaults first (in document order), then the rest of the library. */
  const orderedSections = library.data
    ? [...defaults, ...library.data.sections.filter((s) => !defaults.includes(s))]
    : [];

  function toggle(list: readonly string[], name: string): string[] {
    return list.includes(name) ? list.filter((n) => n !== name) : [...list, name];
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogTitle>Generate contract</DialogTitle>

        {library.isPending ? <LoadingState label="Loading clause library…" /> : null}
        {library.isError ? (
          <ErrorNotice title="Library unavailable" message={errorMessage(library.error)} />
        ) : null}

        {library.data ? (
          <div className="mt-2 space-y-4">
            <SegmentedControl<'standard' | 'virtual' | 'enterprise'>
              label="Contract kind"
              options={
                subject.kind === 'agreement'
                  ? [{ key: 'enterprise', label: 'Enterprise agreement' }]
                  : [
                      { key: 'standard', label: 'Standard (in-person)' },
                      { key: 'virtual', label: 'Virtual' },
                    ]
              }
              value={kind}
              onChange={(next) => {
                setKind(next);
                setCheckedSections(null); // re-seed from the new kind's defaults
              }}
            />

            <Field
              label="Price valid until"
              htmlFor="price-valid-until"
              hint={subject.kind === 'agreement' ? 'Date the client must sign by to hold pricing. Defaults to two weeks from today.' : isCampaign ? 'Date the client must sign by to hold pricing. Defaults to the start of the delivery window.' : 'Date the client must sign by to hold pricing. Defaults to the event date.'}
            >
              <DatePicker
                id="price-valid-until"
                value={priceValidUntil}
                onChange={setPriceValidUntil}
                placeholder={subject.kind === 'agreement' ? 'Defaults to two weeks out' : isCampaign ? 'Defaults to the delivery window' : 'Defaults to the event date'}
              />
            </Field>

            {!isCampaign && series.isPending ? <LoadingState label="Checking Salesforce for a series…" /> : null}
            {series.data?.result === CONTRACT_SERIES_RESULT.unavailable && series.data.reason === CONTRACT_SERIES_UNAVAILABLE.unreachable ? (
              <p className="text-portal-micro text-portal-muted">
                Salesforce couldn’t be checked for a series, so this contract covers only this event.
              </p>
            ) : null}
            {seriesView && seriesView.members.length > 1 ? (
              <SeriesFieldset
                series={seriesView}
                currentEventId={eventId}
                excludedEventIds={excludedEventIds}
                onToggle={(id) => {
                  const next = new Set(excludedEventIds);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  setExcludedEventIds(next);
                }}
              />
            ) : null}

            <fieldset>
              <legend className="text-portal-small font-bold text-portal-ink">
                Clause sections (numbered, in this order)
              </legend>
              <div className="mt-2 grid gap-1">
                {orderedSections.map((name) => (
                  <label key={name} className="flex items-center gap-2 text-portal-small">
                    <Checkbox
                      checked={sections.includes(name)}
                      onCheckedChange={() => setCheckedSections(toggle(sections, name))}
                    />
                    {name}
                  </label>
                ))}
              </div>
            </fieldset>

            {library.data.attachmentB.length > 0 ? (
              <fieldset>
                <legend className="text-portal-small font-bold text-portal-ink">
                  Attachment B — purchaser responsibilities
                </legend>
                {salesforceAttachmentB.length > 0 ? (
                  <p className="mt-0.5 text-portal-micro text-portal-muted">
                    Pre-selected from Salesforce for the program and headcount
                    {coverage.additionalEventIds.length > 0 ? ' of every event on this contract' : ''}.
                  </p>
                ) : null}
                <div className="mt-2 grid grid-cols-2 gap-1">
                  {library.data.attachmentB.map((name) => (
                    <label key={name} className="flex items-center gap-2 text-portal-small">
                      <Checkbox
                        checked={checkedAttachmentB.includes(name)}
                        onCheckedChange={() => setEditedAttachmentB(toggle(checkedAttachmentB, name))}
                      />
                      {name}
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                // Wait for the series, or a series contract would cover one event.
                disabled={!isCampaign && series.isPending}
                onClick={() =>
                  onGenerate({
                    kind,
                    sections: orderedSections.filter((name) => sections.includes(name)),
                    attachmentB: checkedAttachmentB,
                    ...(priceValidUntil ? { priceValidUntil } : {}),
                    ...(coverage.additionalEventIds.length > 0 ? { additionalEventIds: coverage.additionalEventIds } : {}),
                  }, target)
                }
              >
                Generate
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
